import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { SearchDashboardAPIParams } from '@sc/data-access-models';
import { dayjs } from '@sc/utils';
import { ConfigType } from 'dayjs';
import { ButtonComponent } from '../button';
import { DatePickerComponent } from '../date-picker';
import { IconComponent } from '../icon';

@Component({
  selector: 'sc-dashboard-search',
  styleUrls: ['./dashboard-search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    DatePickerComponent,
    IconComponent,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    ButtonComponent,
    NgIf,
    TranslateModule,
  ],
  template: `
    <form [formGroup]="form" class="inputs-row">
      <sc-date-picker
        class="date-picker"
        formControlName="startDate"
        [label]="'Dashboard.search.start' | translate"
        [forceHighlightRequired]="false"
      ></sc-date-picker>

      <sc-icon class="date-separator" name="arrow_right"></sc-icon>

      <sc-date-picker
        class="date-picker"
        formControlName="endDate"
        [label]="'Dashboard.search.end' | translate"
        [forceHighlightRequired]="false"
      ></sc-date-picker>
      <div class="brand-selector">
        <mat-label class="text-grey-dark">{{
          dashboardBrandSelectTitle
            ? dashboardBrandSelectTitle
            : ('Dashboard.search.placeholder' | translate)
        }}</mat-label>
        <mat-form-field>
          <mat-select formControlName="brands" multiple>
            <mat-option *ngFor="let brand of fitterBrands" [value]="brand">{{
              brand.name
            }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <sc-button class="search-button" (click)="search()">
        {{ 'Dashboard.search.actionButton' | translate }}
      </sc-button>
      <div class="export-button" (click)="exportFile()">
        <sc-icon name="export"></sc-icon>
        <span>{{ 'Dashboard.search.exportButton' | translate }}</span>
      </div>
      <sc-button *ngIf="displaySmsStatsBtn" (click)="onSmsExcelBtnClick()">{{
        'Dashboard.search.smsStatsBtn' | translate
      }}</sc-button>
    </form>
  `,
})
export class DashboardSearchBarComponent implements OnInit {
  @Input() fitterBrands: { id: string; name: string; checked?: boolean }[];
  @Input() dashboardBrandSelectTitle?: string;
  @Input() displaySmsStatsBtn = false;

  @Output() clickSearchEvent = new EventEmitter<SearchDashboardAPIParams>();
  @Output() clickExportEvent = new EventEmitter<SearchDashboardAPIParams>();
  @Output() clickSmsStatsEvent = new EventEmitter<SearchDashboardAPIParams>();

  brands = new FormControl();
  public form: FormGroup;

  constructor(
    public elementRef: ElementRef,
    private readonly formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.generateForm();
  }

  getBrandNameByKey(id) {
    return this.fitterBrands?.find((brand) => brand?.id === id)?.name;
  }

  private generateForm(): void {
    const startOfMonth = dayjs().utc().startOf('month').toDate();
    const endOfMonth = dayjs().utc().startOf('day').toDate();
    this.form = this.formBuilder.group({
      startDate: [startOfMonth, [Validators.required]],
      endDate: [endOfMonth, []],
      brands: [
        this.fitterBrands?.filter((brand) => brand?.checked) ?? null,
        [],
      ],
    });
  }

  search() {
    const searchCriteria: SearchDashboardAPIParams = {
      searchDate: {
        start: this.formatStartDate(this.form.value.startDate),
        end: this.formatEndDate(this.form.value.endDate),
      },
      brands: this.form.value.brands?.map((brand) => brand?.id),
    };
    this.clickSearchEvent.emit(searchCriteria);
  }

  exportFile() {
    const searchCriteria: SearchDashboardAPIParams = {
      searchDate: {
        start: this.formatStartDate(this.form.value.startDate),
        end: this.formatEndDate(this.form.value.endDate),
      },
      brands: this.form.value.brands?.map((brand) => brand?.id),
    };
    this.clickExportEvent.emit(searchCriteria);
  }

  formatStartDate(date: ConfigType) {
    return date ? dayjs(date).utc().toDate() : null;
  }

  formatEndDate(date: ConfigType) {
    return date ? dayjs(date).utc().toDate() : null;
  }

  onSmsExcelBtnClick() {
    const searchCriteria: SearchDashboardAPIParams = {
      searchDate: {
        start: this.formatStartDate(this.form.value.startDate),
        end: this.formatEndDate(this.form.value.endDate),
      },
      brands: this.form.value.brands?.map((brand) => brand?.id),
    };
    this.clickSmsStatsEvent.emit(searchCriteria);
  }
}
